import React from "react";
import BasePage from "../../../BasePage";
import Loader from "../../../library/Loader";
import Pagination from "../../../library/Pagination";
import { JobStatus, JobType } from "../../../../apiResources/Job";
import { usePagination } from "../../../../hooks";
import { useJobSearch } from "../../../../api/jobs";
import { SortScope } from "spraypaint";
import { SortDir } from "spraypaint/lib-esm/scope";
import useQueryParam from "../../../../hooks/useQueryParam";
import {
  SearchFilterContainer,
  SearchFilterProvider,
  SearchFilterGroup,
  SearchFilterOption,
} from "../../../library/SearchFilter";
import JobSearchSelectedFilters from "../../../JobSearchSelectedFilters";
import { arrayify } from "../../../../helpers";
import { useSectors } from "../../../../api/sectors";
import { useCurrencies } from "../../../../api/currencies";
import RecruiterJobsTable from "../../../RecruiterJobsTable";
import InfiniteProgress from "../../../library/InfiniteProgress";
import SearchBar from "../../../library/SearchBar";
import SearchInput from "../../../library/SearchInput";
import SettingHelpText from "../../../settings/SettingHelperText";

type Status =
  | JobStatus.NEW
  | JobStatus.OPEN
  | JobStatus.INTERVIEW
  | JobStatus.OFFER;

export type FilterData = {
  status: Status[];
  type: JobType[];
  sector: string[];
  currency: string[];
};

const JobSearch = () => {
  return (
    <BasePage title="Search Jobs">
      <PageContent />
    </BasePage>
  );
};

const PageContent = () => {
  const [searchQuery, setSearchQuery] = useQueryParam("query", "");
  const [sortKey, setSortKey] = useQueryParam("sort", "listedAt");
  const [sortDir, setSortDir] = useQueryParam<SortDir>("sortDir", "desc");
  const [type, setType] = useQueryParam("type", []);
  const [status, setStatus] = useQueryParam("status", []);
  const [sector, setSector] = useQueryParam("sector", []);
  const [currency, setCurrency] = useQueryParam("currency", []);

  const filters = {
    status: arrayify(status),
    type: arrayify(type),
    currency: arrayify(currency),
    sector: arrayify(sector),
  };

  const setFilters = (filters) => {
    setType(filters.type);
    setStatus(filters.status);
    setSector(filters.sector);
    setCurrency(filters.currency);
  };

  const pagination = usePagination();

  const sort: SortScope = {
    [sortKey]: sortDir,
  };

  const { data, isLoading, isFetching } = useJobSearch({
    status: filters.status,
    page: pagination.page,
    per: pagination.per,
    query: searchQuery,
    order: sort,
    jobType: filters.type,
    sector: filters.sector,
    currency: filters.currency,
  });

  const { data: sectors } = useSectors();
  const { data: currencies } = useCurrencies();

  const currencyLabelMap =
    currencies?.reduce((map, currency) => {
      map[currency.id] = currency.label;
      return map;
    }, {}) || {};

  const sectorLabelMap =
    sectors?.reduce((map, sector) => {
      map[sector.id] = sector.name;
      return map;
    }, {}) || {};

  return (
    <SearchFilterProvider
      initialValue={filters}
      onChange={(f) => setFilters(f as FilterData)}
    >
      <SearchBar id="job-filter-bar">
        <SearchInput
          spaceRight
          placeholder="Search by title, location, sector or type"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            pagination.setPage(1);
          }}
        />
        <SearchFilterContainer>
          <SearchFilterGroup label="Job Status" defaultCollapsed={false}>
            <SearchFilterOption
              name="status"
              value={[
                JobStatus.NEW,
                JobStatus.OPEN,
                JobStatus.INTERVIEW,
                JobStatus.OFFER,
              ]}
            >
              Select all
            </SearchFilterOption>
            <SearchFilterOption name="status" value={JobStatus.NEW} nested>
              New
            </SearchFilterOption>
            <SearchFilterOption name="status" value={JobStatus.OPEN} nested>
              Open
            </SearchFilterOption>
            <SearchFilterOption
              name="status"
              value={JobStatus.INTERVIEW}
              nested
            >
              Interviewing
            </SearchFilterOption>
            <SearchFilterOption name="status" value={JobStatus.OFFER} nested>
              Offer made
            </SearchFilterOption>
          </SearchFilterGroup>

          <SearchFilterGroup label="Job Type" defaultCollapsed={false}>
            <SearchFilterOption name="type" value={JobType.PERMANENT}>
              Permanent
            </SearchFilterOption>
            <SearchFilterOption name="type" value={JobType.CONTRACT}>
              Contract
            </SearchFilterOption>
          </SearchFilterGroup>

          <SearchFilterGroup label="Currency" defaultCollapsed={false}>
            {currencies &&
              currencies.map((currency) => (
                <SearchFilterOption
                  key={currency.id}
                  name="currency"
                  value={currency.id}
                >
                  {currency.label}
                </SearchFilterOption>
              ))}
          </SearchFilterGroup>

          <SearchFilterGroup label="Sector" defaultCollapsed={false}>
            {sectors &&
              sectors.map((sector) => (
                <SearchFilterOption
                  key={sector.id}
                  name="sector"
                  value={sector.id}
                >
                  {sector.name}
                </SearchFilterOption>
              ))}
          </SearchFilterGroup>
        </SearchFilterContainer>
      </SearchBar>
      <SettingHelpText>
        Use a comma to search for multiple values e.g. London, Manchester
      </SettingHelpText>
      <JobSearchSelectedFilters
        filters={filters}
        currencyLabelMap={currencyLabelMap}
        sectorLabelMap={sectorLabelMap}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <InfiniteProgress isLoading={isFetching} />
          {data && (
            <>
              <RecruiterJobsTable
                jobs={data.jobs}
                sort={sort}
                onSortChange={(newSort: SortScope) => {
                  setSortKey(Object.keys(newSort)[0]);
                  setSortDir(Object.values(newSort)[0]);
                  pagination.setPage(1);
                }}
              />
              <Pagination pagination={pagination} total={data.meta.total} />
            </>
          )}
        </>
      )}
    </SearchFilterProvider>
  );
};

export default JobSearch;
